import { createSelector } from '@reduxjs/toolkit';
import { matchPath } from 'react-router';
import type { RouterState } from 'redux-first-history';
import type { match } from 'react-router';

interface Options {
  selectRouterState?: <S>(state: S) => RouterState;
  routerReducerKey?: string;
  path: string | string[];
  exact?: boolean;
}

export const createMatchSelector = (options: Options) => {
  let lastPathname: string | null = null;
  let lastMatch: match<Record<string, string>> | null = null;

  let {
    selectRouterState, routerReducerKey = 'router', exact = false, path,
  } = options ?? {};

  if (typeof selectRouterState !== 'function') {
    selectRouterState = <S>(state: S): RouterState => state[routerReducerKey];
  }

  return <S>(state: S): match<Record<string, string>> | null => {
    const routerState = selectRouterState(state);
    const pathname = routerState?.location?.pathname ?? '';

    if (pathname === lastPathname) {
      return lastMatch;
    }

    lastPathname = pathname;
    const match = matchPath(pathname, { path, exact });

    if (!match || !lastMatch || (match.url !== lastMatch.url) || (match.isExact !== lastMatch.isExact)) {
      lastMatch = match;
    }

    return lastMatch;
  };
};

const artistSignupRoutes = [
  '/signup/artist',
  '/signup/artist/create',
  '/signup/artist/photo',
  '/signup/artist/info',
  '/signup/artist/genres',
  '/signup/artist/accounts',
  '/signup/artist/identification',
  '/signup/artist/complete',
];

const hostSignupRoutes = [
  '/signup/host',
  '/signup/host/create',
  '/signup/host/photo',
  '/signup/host/info',
  '/signup/host/space',
  '/signup/host/identification',
  '/signup/host/complete',
];

export const hasNoWindowChromeSelector = createMatchSelector({
  path: ['/signup/intent', '/checkout/signup', '/connect/:showCallId/:profileType/:step', ...artistSignupRoutes, ...hostSignupRoutes],
  exact: true,
});

export const hasDisabledNavigationSelector = createMatchSelector({
  path: ['/connect/:showCallId/:profileType/:step', ...artistSignupRoutes, ...hostSignupRoutes],
  exact: true,
});

const fullScreenRoutes = [
  '/messages',
  '/messages/:threadId',
  '/signup',
  '/signup/:mode',
  '/signup/:mode/:page',
  '/',
  '/login',
  '/login/:oobCode',
  '/home',
  '/for-artists-and-hosts',
  '/for-hosts',
  '/about-us',
  '/showcalls/create',
  '/showcalls/:showCallId/edit',
  '/connect/:showCallId',
  '/connect',
  '/consultation',
  '/o/:jwt',
];

export const isFullScreenSelector = createMatchSelector({
  path: fullScreenRoutes,
  exact: true,
});

export const messagesSelector = createMatchSelector({
  path: ['/messages/:threadId'],
});

export const vanityUrlSelector = createMatchSelector({
  path: ['/:profileType/:vanityUrl'],
});

export const showIdSelector = createMatchSelector({
  path: [
    '/shows/:showId',
    '/myshows/:showId',
    '/myshows/:showId/:showPath',
    '/mytickets/:showId',
    '/reviews/:showId',
    '/admin/shows/:showId/edit',
    '/admin/settlements/:showId',
  ],
});

export const signupRouteSelector = createMatchSelector({
  path: ['/signup', '/signup/intent', ...artistSignupRoutes, ...hostSignupRoutes],
});

export const getThreadIdFromPath = createSelector(
  messagesSelector,
  (match) => {
    const matchThreadId = match && match.params.threadId;

    return matchThreadId || null;
  },
);

export const getShowIdFromPath = createSelector(
  showIdSelector,
  (match) => {
    const showId = match && match.params.showId;

    return showId || null;
  },
);

const jwtSelector = createMatchSelector({
  path: '/o/:jwt',
});

export const getJWTFromPath = createSelector(
  jwtSelector,
  (match) => {
    const jwt = match && match.params.jwt;

    return jwt || null;
  },
);
